import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelName } from '@casops/store/api';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { LibraryItemModel, useDeleteLibraryItemMutation } from '@casops/store/apis/libraryApi';
import { LibraryCompatibleDevicesRenderer } from '@casops/components/tables/cells/library-compatible-devices/LibraryCompatibleDevicesRenderer';
import { useGetLibraryItemTypesQuery } from '@casops/store/apis/settingsApi';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import { useLibraryCompatibleDevicesValueGetter } from '@casops/components/tables/cells/library-compatible-devices/useLibraryCompatibleDevicesValueGetter';
import { useLibraryItemNameValueGetter } from '@casops/components/tables/cells/library-item-name/useLibraryItemNameValueGetter';

type Model = LibraryItemModel;

const canDeleteItem = (data: Model): boolean => !data.used;

interface UseLibraryColdDefsProps {
  modelName: ModelName.LibraryItem;
  permission: Permission;
}

export function useLibraryColDefs({
  modelName,
  permission,
}: UseLibraryColdDefsProps): ColDef<Model>[] {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const { data: libraryItemTypes } = useGetLibraryItemTypesQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const getItemName = useCallback(
    (data: Model): string => {
      const name = libraryItemTypes?.find(({ id }) => id === data.ty)?.n;

      return `${name ?? t('Unknown')} ${t('Fields.Version')} ${data.v}`;
    },
    [t, libraryItemTypes],
  );

  const cellRendererParams = useMemo<ModelActionsCellRendererParams<Model>>(
    () => ({
      modelName,
      permission,
      deleteProps: {
        canDeleteItem,
        getQueryArg: getDefaultModelArg,
        useDeleteModelMutation: useDeleteLibraryItemMutation,
        getItemName,
      },
    }),
    [getItemName, modelName, permission],
  );

  const softwareFirmwareValueGetter = useLibraryItemNameValueGetter<Model>();
  const compatibelDevicesValueGetter = useLibraryCompatibleDevicesValueGetter<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      {
        colId: 'SoftwareFirmware',
        headerName: t('Fields.SoftwareFirmware'),
        valueGetter: softwareFirmwareValueGetter,
        minWidth: 160,
      },
      { colId: 'Version', field: 'v', headerName: t('Fields.Version'), minWidth: 80 },
      {
        colId: 'CompatibleDevices',
        headerName: t('Fields.CompatibleDevices'),
        valueGetter: compatibelDevicesValueGetter, // string[] of translations.
        cellRenderer: LibraryCompatibleDevicesRenderer,
        minWidth: 160, // Min required to fit the column header text.
      },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [
    t,
    isAllowedModelActions,
    cellRendererParams,
    softwareFirmwareValueGetter,
    compatibelDevicesValueGetter,
  ]);

  return columnDefs;
}

export default useLibraryColDefs;
